import { convertPxToRem } from "@/utils";
import { Stack, Typography } from "@mui/material";
import { useTranslation } from "react-i18next";
import FaceScanSuccessDialog from "../FaceScanSuccessDialog";
import FullscreenBackground from "@/features/Common/components/FullscreenBackground";
import IconLoader from "@/features/Common/components/IconLoader";
import Panel from "@/features/Common/components/Panel";

interface FaceScanCompleteProps {
  scanResults: any;
  onSuccessDialogDismiss?: () => void;
  userBMI: number;
}

export default function FaceScanComplete({
  scanResults,
  onSuccessDialogDismiss,
  userBMI,
}: FaceScanCompleteProps) {
  const { t } = useTranslation();

  const isBloodPressureAvailable =
    Boolean(scanResults?.bloodPressure) &&
    Boolean(scanResults?.bloodPressure?.value?.systolic) &&
    Boolean(scanResults?.bloodPressure?.value?.diastolic);

  const isHeartRateAvailable =
    Boolean(scanResults?.pulseRate) && Boolean(scanResults?.pulseRate?.value);

  const processedScanResults = [
    {
      value: `${scanResults?.bloodPressure?.value?.systolic}/${scanResults?.bloodPressure?.value?.diastolic}`,
      shouldShow: isBloodPressureAvailable,
      code: "BLOOD_PRESSURE",
    },
    {
      value: scanResults?.pulseRate?.value,
      shouldShow: isHeartRateAvailable,
      code: "PULSE",
    },
    {
      value: userBMI,
      shouldShow: true,
      code: "BMI",
    },
  ];

  const shouldShowResults = processedScanResults.some(
    (resultEntry) => resultEntry.shouldShow === true
  );

  const resultsHeading = shouldShowResults
    ? t("BinahFaceScanSuccess.title")
    : t("common.genericErrorMessage");

  const resultsFontSize = shouldShowResults
    ? convertPxToRem(54)
    : convertPxToRem(135);

  const resultsBackgroundColor = shouldShowResults
    ? "neutral.50"
    : "primary.main";

  return (
    <Stack color="primary.contrastText" justifyContent="flex-end">
      <Stack
        sx={{
          zIndex: 1,
          position: "relative",
        }}
        bgcolor="primary.main"
        textAlign="center"
        p={4}
        spacing={2}
        px={6}
      >
        <IconLoader
          style={{
            alignSelf: "center",
            fontSize: resultsFontSize,
          }}
          color="primary"
          icon="FaceScanInverse"
        />

        <Typography variant="h2" color="inherit">
          {resultsHeading}
        </Typography>
      </Stack>

      {shouldShowResults && (
        <Stack
          sx={{
            width: "100%",
            flex: 1,
            display: "flex",
            backgroundColor: "neutral.50",
            position: "relative",
            borderTopLeftRadius: 20,
            borderTopRightRadius: 20,
            p: 2,
            pb: 15,
            zIndex: 2,
            textAlign: "center",
            top: -16,
          }}
        >
          <Typography pb={2} variant="h3" fontWeight={600}>
            {t("BinahFaceScanSuccess.resultsTitle")}
          </Typography>
          {(processedScanResults || []).map((result: any, index: number) => (
            <Stack my={1} key={result.code + index}>
              {result?.shouldShow && (
                <Panel>
                  <Stack spacing={2} justifyContent="space-between">
                    <Stack direction="row" alignItems="center">
                      <IconLoader
                        style={{
                          alignSelf: "center",
                          fontSize: convertPxToRem(34),
                          marginRight: 2,
                        }}
                        color="primary"
                        icon="HealthPin"
                      />
                      <Typography
                        variant="h2"
                        fontWeight={600}
                        color="primary.700"
                      >
                        {result?.value}
                      </Typography>
                    </Stack>
                    <Stack textAlign="left" spacing={1}>
                      <Typography variant="h4" fontWeight={600}>
                        {t(
                          `BinahFaceScanSuccess.results.${result?.code}.label` as any
                        )}
                      </Typography>
                      <Typography variant="body2" color="grey.700">
                        {t(
                          `BinahFaceScanSuccess.results.${result?.code}.description` as any
                        )}
                      </Typography>
                    </Stack>
                  </Stack>
                </Panel>
              )}
            </Stack>
          ))}
        </Stack>
      )}
      <FullscreenBackground color={resultsBackgroundColor} />
      <FaceScanSuccessDialog onDismiss={onSuccessDialogDismiss} />
    </Stack>
  );
}
